import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  MenuItem,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import BlockIcon from '@mui/icons-material/Block';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PageTitleWrapper from '@/components/PageTitleWrapper';
import { useUserViewMode } from '@/hooks/useUserViewMode';
import { branchService } from '@/data/branchService';
import { companyService } from '@/data/companyService';
import { foodDeliveryService } from '@/data/foodDeliveryService';
import {
  BulkIntegrationAction,
  CompanyMonitoring,
  EXTERNAL_INTEGRATION_STATUS_LABELS,
  ExternalIntegrationStatus,
  FOOD_ALERT_CODE_LABELS,
  FOOD_ALERT_SEVERITY_COLOR,
  FOOD_PROVIDER_LABELS,
  FoodIntegration
} from '@/types/FoodDelivery.interface';
import FoodDeliveryEmptyState from '../food-delivery/components/FoodDeliveryEmptyState';
import { Company } from '@/types/Company.interface';

const WINDOW_OPTIONS = [
  { value: 24, label: 'Son 24 saat' },
  { value: 72, label: 'Son 3 gün' },
  { value: 168, label: 'Son 7 gün' }
];

const INTEGRATION_STATUS_COLOR: Record<
  ExternalIntegrationStatus,
  'success' | 'error' | 'warning' | 'default' | 'info'
> = {
  [ExternalIntegrationStatus.ACTIVE]: 'success',
  [ExternalIntegrationStatus.ERROR]: 'error',
  [ExternalIntegrationStatus.PAUSED]: 'warning',
  [ExternalIntegrationStatus.PENDING]: 'info',
  [ExternalIntegrationStatus.DISABLED]: 'default'
};

const ACTION_LABEL: Record<BulkIntegrationAction, string> = {
  [BulkIntegrationAction.PAUSE]: 'Duraklat',
  [BulkIntegrationAction.RESUME]: 'Devam ettir',
  [BulkIntegrationAction.DISABLE]: 'Devre dışı bırak',
  [BulkIntegrationAction.REVALIDATE]: 'Yeniden doğrula'
};

const fmtDate = (iso: string | null): string =>
  iso ? new Date(iso).toLocaleString('tr-TR') : '—';

const Stat: React.FC<{
  label: string;
  value: React.ReactNode;
  color?: string;
}> = ({ label, value, color }) => (
  <Box sx={{ textAlign: 'center', px: 2, py: 1 }}>
    <Typography variant="h3" sx={{ color }}>
      {value}
    </Typography>
    <Typography variant="caption" color="textSecondary">
      {label}
    </Typography>
  </Box>
);

/**
 * Faz 11 (ürünleştirme/ölçekleme): çok-şube (company-wide) görünüm + toplu
 * entegrasyon yönetimi. SUPER_ADMIN şirket seçer; COMPANY_ADMIN/BRANCH_ADMIN
 * kendi şirketinde çalışır (kapsam backend FoodScopeService ile doğrulanır —
 * bu ekran yalnızca UI kolaylığıdır, güvenliğin yerine geçmez).
 */
const FoodDeliveryBulk: React.FC = () => {
  const { isSuperAdmin, company } = useUserViewMode();

  const [companies, setCompanies] = useState<Company[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(
    !isSuperAdmin && company ? company.id : null
  );
  const [windowHours, setWindowHours] = useState<number>(24);

  const [monitoring, setMonitoring] = useState<CompanyMonitoring | null>(null);
  const [integrations, setIntegrations] = useState<FoodIntegration[]>([]);
  const [branchNames, setBranchNames] = useState<Map<number, string>>(
    new Map()
  );
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState(false);
  const [confirm, setConfirm] = useState<BulkIntegrationAction | null>(null);
  const [toast, setToast] = useState<{
    msg: string;
    severity: 'success' | 'error' | 'info';
  } | null>(null);

  // SUPER_ADMIN için şirket listesi.
  useEffect(() => {
    if (!isSuperAdmin) return;
    companyService
      .getAllFlat({ getAll: true })
      .then(setCompanies)
      .catch(() =>
        setToast({ msg: 'Şirketler getirilemedi.', severity: 'error' })
      );
  }, [isSuperAdmin]);

  const load = useCallback(async () => {
    if (!companyId) {
      setMonitoring(null);
      setIntegrations([]);
      return;
    }
    setLoading(true);
    setSelected(new Set());
    try {
      const [mon, list, branches] = await Promise.all([
        foodDeliveryService.getCompanyMonitoring(companyId, windowHours),
        foodDeliveryService.listByCompany(companyId),
        branchService.getAllFlat({ companyId, getAll: true })
      ]);
      setMonitoring(mon);
      setIntegrations(list);
      setBranchNames(new Map(branches.map((b) => [b.id, b.name])));
    } catch {
      setToast({ msg: 'Veriler getirilemedi.', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [companyId, windowHours]);

  useEffect(() => {
    load();
  }, [load]);

  const branchLabel = useCallback(
    (branchId: number) => branchNames.get(branchId) ?? `Şube #${branchId}`,
    [branchNames]
  );

  const allSelected =
    integrations.length > 0 && selected.size === integrations.length;
  const someSelected = selected.size > 0 && !allSelected;

  const toggleAll = () => {
    setSelected(
      allSelected ? new Set() : new Set(integrations.map((i) => i.id))
    );
  };
  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const runBulk = async (action: BulkIntegrationAction) => {
    setConfirm(null);
    if (selected.size === 0) return;
    setActing(true);
    try {
      const res = await foodDeliveryService.bulkAction(action, [...selected]);
      const parts = [`${res.succeeded.length} başarılı`];
      if (res.failed.length) parts.push(`${res.failed.length} başarısız`);
      if (res.notFound.length) parts.push(`${res.notFound.length} bulunamadı`);
      setToast({
        msg: `${ACTION_LABEL[action]}: ${parts.join(', ')}.`,
        severity: res.failed.length ? 'info' : 'success'
      });
      await load();
    } catch {
      setToast({ msg: 'Toplu işlem başarısız.', severity: 'error' });
    } finally {
      setActing(false);
    }
  };

  const summary = monitoring?.overview.summary;
  const branchRows = monitoring?.overview.branches ?? [];
  const alerts = monitoring?.alerts ?? [];

  const bulkButtons = useMemo(
    () =>
      [
        {
          action: BulkIntegrationAction.PAUSE,
          icon: <PauseCircleOutlineIcon />,
          color: 'warning' as const
        },
        {
          action: BulkIntegrationAction.RESUME,
          icon: <PlayCircleOutlineIcon />,
          color: 'success' as const
        },
        {
          action: BulkIntegrationAction.REVALIDATE,
          icon: <VerifiedOutlinedIcon />,
          color: 'primary' as const
        },
        {
          action: BulkIntegrationAction.DISABLE,
          icon: <BlockIcon />,
          color: 'error' as const
        }
      ] as const,
    []
  );

  return (
    <Box sx={{ width: '100%' }}>
      <PageTitleWrapper>
        <Box>
          <Typography variant="h3" component="h3" gutterBottom>
            Toplu Yönetim (Çok Şube)
          </Typography>
          <Typography variant="subtitle2">
            Şirket genelindeki tüm şubelerin yemek platformu entegrasyonlarını
            tek ekranda izleyin ve toplu işlem uygulayın (duraklat / devam /
            yeniden doğrula / devre dışı).
          </Typography>
        </Box>
      </PageTitleWrapper>

      <Box sx={{ px: 4, pb: 4 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ md: 'center' }}
            >
              {isSuperAdmin && (
                <Autocomplete
                  sx={{ minWidth: 280 }}
                  options={companies}
                  getOptionLabel={(c) => c.name}
                  value={companies.find((c) => c.id === companyId) ?? null}
                  onChange={(_e, val) => setCompanyId(val?.id ?? null)}
                  renderInput={(params) => (
                    <TextField {...params} label="Şirket seç" />
                  )}
                />
              )}
              <TextField
                select
                label="Zaman aralığı"
                value={windowHours}
                onChange={(e) => setWindowHours(Number(e.target.value))}
                sx={{ minWidth: 180 }}
              >
                {WINDOW_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </TextField>
              <Box sx={{ flexGrow: 1 }} />
              <Tooltip title="Yenile">
                <span>
                  <IconButton
                    aria-label="Yenile"
                    onClick={load}
                    disabled={!companyId || loading}
                  >
                    {loading ? <CircularProgress size={22} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </CardContent>
        </Card>

        {!companyId ? (
          <Alert severity="info">
            {isSuperAdmin
              ? 'Görüntülemek için bir şirket seçin.'
              : 'Şirket bilgisi bulunamadı.'}
          </Alert>
        ) : loading && !monitoring ? (
          <Box display="flex" justifyContent="center" py={6}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Şirket özeti */}
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h4" gutterBottom>
                  Şirket Özeti
                </Typography>
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  justifyContent="space-around"
                >
                  <Stat label="Şube" value={summary?.branches ?? 0} />
                  <Stat label="Entegrasyon" value={summary?.total ?? 0} />
                  <Stat
                    label="Aktif"
                    value={summary?.active ?? 0}
                    color="green"
                  />
                  <Stat
                    label="Hatalı"
                    value={summary?.error ?? 0}
                    color={summary && summary.error > 0 ? 'red' : undefined}
                  />
                  <Stat
                    label="Duraklatıldı"
                    value={summary?.paused ?? 0}
                    color={summary && summary.paused > 0 ? 'orange' : undefined}
                  />
                  <Stat label="Beklemede" value={summary?.pending ?? 0} />
                  <Stat label="Devre dışı" value={summary?.disabled ?? 0} />
                </Stack>
              </CardContent>
            </Card>

            {/* Uyarılar */}
            {alerts.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    Uyarılar ({alerts.length})
                  </Typography>
                  <Stack spacing={1}>
                    {alerts.map((a, idx) => (
                      <Alert
                        key={`${a.code}-${a.integrationId ?? 'co'}-${idx}`}
                        severity={FOOD_ALERT_SEVERITY_COLOR[a.severity]}
                      >
                        <AlertTitle sx={{ mb: 0 }}>
                          {FOOD_ALERT_CODE_LABELS[a.code]}
                          {a.integrationId
                            ? ` · entegrasyon #${a.integrationId}`
                            : ''}
                        </AlertTitle>
                        {a.message}
                      </Alert>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Şube bazlı durum kırılımı */}
            {branchRows.length > 0 && (
              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h4" gutterBottom>
                    Şube Kırılımı
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Şube</TableCell>
                          <TableCell align="right">Toplam</TableCell>
                          <TableCell align="right">Aktif</TableCell>
                          <TableCell align="right">Hatalı</TableCell>
                          <TableCell align="right">Duraklatıldı</TableCell>
                          <TableCell align="right">Beklemede</TableCell>
                          <TableCell align="right">Devre dışı</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {branchRows.map((b) => (
                          <TableRow key={b.branchId} hover>
                            <TableCell>{branchLabel(b.branchId)}</TableCell>
                            <TableCell align="right">{b.total}</TableCell>
                            <TableCell align="right">{b.active}</TableCell>
                            <TableCell align="right">{b.error}</TableCell>
                            <TableCell align="right">{b.paused}</TableCell>
                            <TableCell align="right">{b.pending}</TableCell>
                            <TableCell align="right">{b.disabled}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Entegrasyonlar + toplu işlem */}
            <Card>
              <Toolbar
                sx={{
                  pl: 2,
                  pr: 1,
                  ...(selected.size > 0 && {
                    bgcolor: 'action.selected'
                  })
                }}
              >
                {selected.size > 0 ? (
                  <Typography sx={{ flex: 1 }} variant="subtitle1">
                    {selected.size} entegrasyon seçildi
                  </Typography>
                ) : (
                  <Typography sx={{ flex: 1 }} variant="h4">
                    Entegrasyonlar ({integrations.length})
                  </Typography>
                )}
                <Stack direction="row" spacing={1}>
                  {bulkButtons.map((b) => (
                    <Button
                      key={b.action}
                      size="small"
                      variant="outlined"
                      color={b.color}
                      startIcon={b.icon}
                      disabled={selected.size === 0 || acting}
                      onClick={() => setConfirm(b.action)}
                    >
                      {ACTION_LABEL[b.action]}
                    </Button>
                  ))}
                </Stack>
              </Toolbar>
              <Divider />
              <CardContent sx={{ pt: 0 }}>
                {integrations.length === 0 ? (
                  <Box sx={{ mt: 2 }}>
                    <FoodDeliveryEmptyState
                      title="Entegrasyon yok"
                      message="Bu şirkette (erişebildiğiniz şubelerde) yemek platformu entegrasyonu yok. Toplu işlem yapabilmek için önce şube bazında entegrasyon oluşturun."
                    />
                  </Box>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              indeterminate={someSelected}
                              checked={allSelected}
                              onChange={toggleAll}
                            />
                          </TableCell>
                          <TableCell>Şube</TableCell>
                          <TableCell>Platform</TableCell>
                          <TableCell>Durum</TableCell>
                          <TableCell>Aktif</TableCell>
                          <TableCell>Son Hata</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {integrations.map((i) => {
                          const checked = selected.has(i.id);
                          return (
                            <TableRow
                              key={i.id}
                              hover
                              selected={checked}
                              onClick={() => toggleOne(i.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox checked={checked} />
                              </TableCell>
                              <TableCell>{branchLabel(i.branchId)}</TableCell>
                              <TableCell>
                                {FOOD_PROVIDER_LABELS[i.providerCode]}
                                {i.displayName ? ` — ${i.displayName}` : ''}
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  color={INTEGRATION_STATUS_COLOR[i.status]}
                                  label={
                                    EXTERNAL_INTEGRATION_STATUS_LABELS[i.status]
                                  }
                                />
                              </TableCell>
                              <TableCell>
                                {i.isActive ? 'Evet' : 'Hayır'}
                              </TableCell>
                              <TableCell>
                                {i.lastErrorMessage ? (
                                  <Tooltip title={i.lastErrorMessage}>
                                    <span>{fmtDate(i.lastErrorAt)}</span>
                                  </Tooltip>
                                ) : (
                                  '—'
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      {/* Toplu işlem onayı */}
      <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
        <DialogTitle>Toplu işlem onayı</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm
              ? `${selected.size} entegrasyon için "${ACTION_LABEL[confirm]}" işlemini uygulamak istiyor musunuz?`
              : ''}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Vazgeç</Button>
          <Button
            variant="contained"
            disabled={acting}
            onClick={() => confirm && runBulk(confirm)}
          >
            {acting ? <CircularProgress size={20} /> : 'Uygula'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            variant="filled"
          >
            {toast.msg}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default FoodDeliveryBulk;
